import { z } from 'zod';

/**
 * Canonical visibility set used for new writes.
 *
 * - `public`     — anyone in the org
 * - `area`       — creator's area + areas listed in `sharedWithAreas`
 * - `multi-area` — explicit list in `sharedWithAreas` only (creator's area
 *                  does NOT get implicit access)
 * - `shared`     — explicit list of users in `sharedWith`
 * - `personal`   — creator only
 *
 * The legacy GAS used `private` for what we now call `personal`. Reads tolerate
 * the old label via `resourceVisibilityRead` below; new writes always use the
 * canonical names.
 */
export const resourceVisibilitySchema = z.enum([
  'public',
  'area',
  'multi-area',
  'shared',
  'personal',
]);
export type ResourceVisibility = z.infer<typeof resourceVisibilitySchema>;

/** Internal: tolerant parser for reading legacy docs that still carry `private`. */
const resourceVisibilityRead = z.union([
  resourceVisibilitySchema,
  z.literal('private').transform(() => 'personal' as const),
]);

export const resourceReactionsSchema = z.object({
  thumbsUp: z.array(z.string().email()).default([]),
  fire: z.array(z.string().email()).default([]),
  heart: z.array(z.string().email()).default([]),
});
export type ResourceReactions = z.infer<typeof resourceReactionsSchema>;

export const resourceReactionTypeSchema = z.enum(['thumbsUp', 'fire', 'heart']);
export type ResourceReactionType = z.infer<typeof resourceReactionTypeSchema>;

export const resourceSourceSchema = z.enum([
  'manual',
  'extension',
  'github',
  'gitlab',
  'bitbucket',
  'jira',
  'confluence',
  'slack',
  'teams',
  'notion',
  'metabase',
  'grafana',
  'tableau',
  'mysql',
  'postgresql',
  'mongodb',
  'api',
  'webhook',
]);
export type ResourceSource = z.infer<typeof resourceSourceSchema>;

/**
 * Client-facing subset of `resourceSourceSchema`. Anything written by a
 * human-driven client (web modal, browser extension) must come from this
 * narrower allowlist — values like `github`, `webhook`, `notion`, etc. are
 * reserved for server-side integration paths (webhook ingestion, syncs)
 * where the source is provable instead of self-declared.
 *
 * Treating `source` as an authoritative field — instead of a UX hint — is
 * what lets the audit log / analytics in the hub trust that
 * `source: 'webhook'` actually came from a webhook and not from a user
 * faking the field via devtools.
 */
export const clientResourceSourceSchema = z.enum(['manual', 'extension']);
export type ClientResourceSource = z.infer<typeof clientResourceSourceSchema>;

/**
 * Read-side URL schema — permissive to keep legacy resources from the GAS
 * migration parseable. Legacy docs sometimes lack a scheme (e.g. `example.com`).
 */
const urlStringRead = z
  .string()
  .trim()
  .min(4)
  .refine((v) => !v.includes(' '), { message: 'URL cannot contain spaces' })
  .refine((v) => v.includes('.'), { message: 'URL must contain a domain' });

/**
 * Write-side URL schema — adds a hard `http(s)` scheme requirement so
 * malicious clients can't sneak `javascript:`, `data:`, `file:`, etc. into
 * the hub. Anything saved through this schema is safe to use as a
 * navigation target later. Applied at the contract boundary so it covers
 * the web modal, the extension popup, and any future ingestion path.
 */
const urlStringWrite = urlStringRead.refine((v) => /^https?:\/\//i.test(v), {
  message: 'URL must start with http:// or https://',
});

export const resourceSchema = z.object({
  id: z.string(),
  title: z.string().min(1).max(200),
  url: urlStringRead,
  category: z.string().min(1),
  description: z.string().max(2000).default(''),
  icon: z.string().max(8).default('🔗'),
  tags: z.array(z.string().min(1).max(40)).default([]),
  clicks: z.number().int().nonnegative().default(0),
  isActive: z.boolean().default(true),
  // Read-side parser tolerates the legacy `private` label and maps it to
  // `personal` so consumers always see the canonical value.
  visibility: resourceVisibilityRead.default('area'),
  owner: z.string().default(''),
  creatorArea: z.string().default('DATA'),
  sharedWith: z.array(z.string().email()).default([]),
  sharedWithAreas: z.array(z.string()).default([]),
  favorites: z.array(z.string().email()).default([]),
  reactions: resourceReactionsSchema.default({ thumbsUp: [], fire: [], heart: [] }),
  source: resourceSourceSchema.default('manual'),
  webhookId: z.string().nullable().default(null),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
  createdBy: z.string().default(''),
});
export type Resource = z.infer<typeof resourceSchema>;

/**
 * Legacy-tolerant read schema. Some legacy resources (in particular those
 * created by the GAS extension handler in `Código.gs:1700-1725`) never wrote
 * `updatedAt`. Falling back to `createdAt` keeps them visible.
 */
export const resourceReadSchema = z.preprocess((raw) => {
  if (typeof raw !== 'object' || raw === null) return raw;
  const obj = raw as Record<string, unknown>;
  const out: Record<string, unknown> = { ...obj };
  if (out.updatedAt === undefined && typeof out.createdAt === 'string') {
    out.updatedAt = out.createdAt;
  }
  return out;
}, resourceSchema);

export const createResourceSchema = z.object({
  title: z.string().trim().min(1).max(200),
  url: urlStringWrite,
  category: z.string().min(1),
  description: z.string().max(2000).optional(),
  icon: z.string().max(8).optional(),
  tags: z.array(z.string().min(1).max(40)).max(20).optional(),
  visibility: resourceVisibilitySchema.optional(),
  creatorArea: z.string().optional(),
  // Lowercase emails on input so casing inconsistencies (e.g. `Foo@kiwibot.com`
  // from the user-lite directory) never leak into Firestore docs.
  sharedWith: z
    .array(
      z
        .string()
        .email()
        .transform((e) => e.toLowerCase()),
    )
    .optional(),
  sharedWithAreas: z.array(z.string()).optional(),
  // Server-trusted source values only. See `clientResourceSourceSchema`.
  source: clientResourceSourceSchema.optional(),
});
export type CreateResourceInput = z.infer<typeof createResourceSchema>;

export const updateResourceSchema = createResourceSchema.partial().extend({
  isActive: z.boolean().optional(),
});
export type UpdateResourceInput = z.infer<typeof updateResourceSchema>;

export const resourceQuerySchema = z.object({
  category: z.string().optional(),
  search: z.string().optional(),
  source: resourceSourceSchema.optional(),
  visibility: resourceVisibilitySchema.optional(),
  isActive: z.coerce.boolean().optional(),
  /** Page size, default 200, max 500. Phase 8 bump from the legacy 50 because
   * the GAS-era hub already had ~152 resources and silently truncating the
   * tail looked like "missing" data to users. */
  limit: z.coerce.number().int().positive().max(500).optional().default(200),
  /** Pagination cursor — last `id` of the previous page. */
  cursor: z.string().optional(),
});
export type ResourceQuery = z.infer<typeof resourceQuerySchema>;
